/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { dropTask, restartableTask } from 'ember-concurrency';
import moment from 'moment-timezone';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { dateCriticality } from 'volta/utils/style-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { TError } from 'volta/utils/error-utils';
import type { IResourceName } from 'volta/models/base-model';
import type { IAggTasksViewMap, TTaskType, UTask } from 'volta/models/types/tasks';
import type TaskService from 'volta/services/task-service';
import type StoreService from 'volta/services/store';
import type { ComponentLike } from '@glint/template';
import type { TasksTriggerBtnSignature } from './trigger-btn';
import type { TDate } from 'volta/models/types/calendar';

export interface TasksSignature {
  Args: {
    taskType: TTaskType;
    entityId: string | null;
    entityResourceName: IResourceName;
    aggStats?: IAggTasksViewMap;
    disabled?: boolean;
    disabledEdition?: boolean;
    minimized?: boolean;
    inPage?: boolean;
    groupingField?: 'dueDate';
    isAutoResolvable?: boolean;
    scrollToDate?: TDate;
    showDueDate?: boolean;
    authorizedUsers?: string[];
    onTaskUpdated?: (task: UTask[]) => void;
    onClose?: () => void;
  };
  Blocks: {
    default: [
      {
        visible: boolean;
        show: () => void;
        hide: () => void;
        toggle: () => void;
        onTrigger: () => void;
        TriggerBtn: ComponentLike<TasksTriggerBtnSignature>;
      }
    ];
  };
  Element: HTMLElement;
}

export default class TasksComponent extends Component<TasksSignature> {
  @service store!: StoreService;
  @service taskService!: TaskService;

  @tracked tasks: UTask[] = [];

  get allowedDuedTaskTypes(): string[] {
    return [this.args.taskType];
  }

  get isLoading(): boolean {
    return this.fetchTasks.isRunning;
  }

  get tasksCount(): number {
    const { aggStats } = this.args;

    if (!aggStats && this.tasks.filter((task) => !task.resolvedAt).length === 0) {
      return 0;
    }

    return Object.entries(aggStats ?? {}).reduce((acc, [taskType, aggStats]) => {
      if (!aggStats) {
        return acc;
      }
      if (this.allowedDuedTaskTypes.includes(taskType)) {
        acc += aggStats.total;
      }
      return acc;
    }, 0);
  }

  get aggStatsMinDueDate(): TDate | undefined {
    const { aggStats } = this.args;
    if (!aggStats) {
      return undefined;
    }

    return Object.entries(aggStats).reduce((acc: string | undefined, [taskType, aggStats]) => {
      if (!aggStats) {
        return acc;
      }
      if (this.allowedDuedTaskTypes.includes(taskType) && aggStats.minDueDate) {
        if (!acc) {
          acc = aggStats.minDueDate;
        } else if (moment.utc(aggStats.minDueDate).isBefore(acc)) {
          acc = aggStats.minDueDate;
        }
      }
      return acc;
    }, undefined);
  }

  get criticality() {
    const { aggStats } = this.args;

    if (!aggStats) {
      return undefined;
    }
    return dateCriticality(this.aggStatsMinDueDate);
  }

  fetchTasks = restartableTask(async () => {
    if (!this.args.entityId) {
      return [];
    }
    try {
      switch (this.args.taskType) {
        case 'PLANNING_TASK': {
          this.tasks = await this.taskService.fetchPlanningTasks.perform(this.args.entityId);
          return;
        }
        case 'EXECUTION_TASK': {
          this.tasks = await this.taskService.fetchExecutionTasks.perform(this.args.entityId);
          return;
        }
        case 'SKU_TASK': {
          this.tasks = await this.taskService.fetchSkuTasks.perform(this.args.entityId);
          return;
        }
        case 'FORECAST_TASK': {
          this.tasks = await this.taskService.fetchForecastsTasks.perform(this.args.entityId);
          return;
        }
        case 'SIMULATION_TASK': {
          this.tasks = await this.taskService.fetchSimulationTasks.perform(this.args.entityId);
          return;
        }
        case 'SIMULATION_ITEM_TASK': {
          this.tasks = await this.taskService.fetchSimulationItemSettingTasks.perform(
            this.args.entityId
          );
          return;
        }
        case 'FORECAST_PROJECT_TASK': {
          this.tasks = await this.taskService.fetchForecastProjectTasks.perform(
            this.args.entityId
          );
          return;
        }
        default: {
          return [];
        }
      }
    } catch (error) {
      return defaultErrorHandler(error as TError);
    }
  });

  @action
  onDraftTask(task?: UTask) {
    if (task) {
      this.tasks = [...this.tasks, task];
      this.args.onTaskUpdated?.(this.tasks);
    }
  }

  @action
  onSaveTask(task?: UTask) {
    this.tasks = this.tasks
      .filter((t) => t?.id && t.id !== task?.id)
      .concat(task?.id ? [task] : []);
    this.args.onTaskUpdated?.(this.tasks);
  }

  @action
  onDeleteTask(task?: UTask) {
    const withoutDeleted = this.tasks.filter((t) => t?.id && t.id !== task?.id);
    if (withoutDeleted.length !== this.tasks.length) {
      this.tasks = withoutDeleted;
      this.args.onTaskUpdated?.(this.tasks);
    }
  }

  onCloseTasks = dropTask(async (task?: UTask) => {
    if (!task) {
      return;
    }
    if (task.newWithoutComment) {
      this.onDeleteTask(task);
    } else {
      await this.taskService.saveTask.perform(task);
    }
  });
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    Tasks: typeof TasksComponent;
    tasks: typeof TasksComponent;
  }
}
